import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate, useRouterState } from '@tanstack/react-router';
import { Building2, CalendarClock, Flag, Hourglass, Plus, SearchX, TriangleAlert, Upload } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { Seletor } from '@/componentes/Seletor';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarCompetencia, formatarDataHora, formatarDia, formatarInteiro, formatarReais } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import { apiDasIntegracoes, useConfiguracaoDaTroca, useCorrespondencias, useDivergencias, useLotes, useMatriz, useResumoDasIntegracoes, useSistemas } from './api';
import {
  ABAS_DAS_INTEGRACOES,
  CADASTROS_DE_ENTRADA,
  ROTULO_DA_ENTIDADE,
  ROTULO_DA_RESOLUCAO,
  ROTULO_DO_TIPO_DE_DIVERGENCIA,
  ROTULO_DO_TIPO_DE_SISTEMA,
  SITUACAO_DA_LINHA,
  SITUACAO_DO_LOTE,
  alvoDaDivergencia,
  diferencaEscrita,
  fraseDaLinha,
  mestresDeHoje,
  retornoDoResponsavel,
  trocaEscrita,
  vigenciaEscrita,
  type CadastroDeEntrada,
  type Correspondencia,
  type DescricaoDaEntidade,
  type Divergencia,
  type Entidade,
  type LinhaDaMatriz,
  type Lote,
} from './integracoes';
import {
  GavetaDaProposta,
  JanelaDaSaida,
  JanelaDaTolerancia,
  JanelaDeCorrespondencia,
  JanelaDeDecisao,
  JanelaDeEncerramento,
  JanelaDeImportacao,
  JanelaDeResolucao,
  JanelaDeSistema,
  JanelaDeTroca,
} from './JanelasDasIntegracoes';
import { usePermissoesDaIntegracao } from './permissoes';
import { EtiquetaDaIntegracao, SeloDaIntegracao } from './Selo';

/**
 * Módulo 04 do v6 — INTEGRAÇÕES E CONCILIAÇÃO, a parte do ERP, na mesa (0104).
 *
 * Um hub com quatro abas, cada uma com endereço próprio, como a telemetria: a
 * MATRIZ de sistema mestre (quem é o mestre de cada entidade, a troca e a
 * contingência), os LOTES (o que entrou e saiu, com a conferência), as
 * CORRESPONDÊNCIAS (o de-para da chave do sistema de fora com o cadastro da
 * plataforma) e as DIVERGÊNCIAS (o retorno contra o que saiu). O lote abre em
 * página própria, com cada linha e o erro dela.
 *
 * Não há tela-modelo da parte do ERP do módulo 04 no manual (o mapa das p.50 a
 * p.60 desenha só a conciliação de horas): segue o hub com abas (p.40), a lista
 * com detalhe (p.38), a conferência antes de valer (p.43) e a aprovação com
 * trilha (p.44) — dúvida de design registrada.
 */

const EXPLICACAO: Readonly<Record<(typeof ABAS_DAS_INTEGRACOES)[number]['para'], string>> = {
  '/cadastros/integracoes':
    'Por entidade, quem é o sistema mestre, com quem a troca acontece e o que fazer se ela falhar. Sem linha aprovada e vigente não há lote, e o fiscal nunca é da plataforma.',
  '/cadastros/integracoes/lotes':
    'Cada arquivo que entrou ou saiu. A entrada fica em conferência até alguém promover; a saída espera o retorno do sistema de fora.',
  '/cadastros/integracoes/titulos':
    'Os títulos a pagar e a receber, as baixas e as notas (só como referência) que vêm do mestre de cada um. Com o mestre de fora, a plataforma só escreve em contingência, e o lote seguinte casa.',
  '/cadastros/integracoes/correspondencias':
    'A chave de cada registro no sistema de fora ao lado do cadastro da plataforma. É ela que faz o mesmo arquivo de novo não criar nada repetido.',
  '/cadastros/integracoes/divergencias':
    'O retorno do sistema de fora contra o que saiu da plataforma: linha só de um lado, valor diferente e total da conta contábil que não bate.',
};

function abaDoCaminho(caminho: string): (typeof ABAS_DAS_INTEGRACOES)[number] {
  return [...ABAS_DAS_INTEGRACOES].reverse().find((a) => caminho === a.para || caminho.startsWith(`${a.para}/`)) ?? ABAS_DAS_INTEGRACOES[0];
}

export function ErroDaIntegracao({ oQue, erro, aoTentar }: { readonly oQue: string; readonly erro: unknown; readonly aoTentar: () => void }) {
  return (
    <EstadoVazio
      icone={TriangleAlert}
      tom="erro"
      titulo={`Não foi possível carregar ${oQue}`}
      descricao={explicarFalha(erro).comoResolver}
      acao={<Botao nivel="secundario" onClick={aoTentar}>Carregar de novo</Botao>}
    />
  );
}

/* ================================================================ o hub */

export function HubDasIntegracoes({ children, acoes }: { readonly children: ReactNode; readonly acoes?: ReactNode }) {
  const caminho = useRouterState({ select: (e) => e.location.pathname });
  const navegar = useNavigate();
  const aba = abaDoCaminho(caminho);
  const resumo = useResumoDasIntegracoes();
  const r = resumo.data;
  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Cadastros e governança"
          titulo="Integrações"
          subtitulo="A troca com o ERP: quem é o mestre de cada dado, o que entrou, o que saiu e o que não bateu"
          {...(acoes ? { acoes } : {})}
        />
        <FaixaIndicadores>
          <CardIndicador
            rotulo="Linhas vigentes"
            valor={r ? formatarInteiro(r.vigentes) : ''}
            contexto={r ? `${formatarInteiro(r.propostas)} ${r.propostas === 1 ? 'proposta esperando decisão' : 'propostas esperando decisão'}` : 'entidades com mestre aprovado hoje'}
            icone={Building2}
            tom="marca"
            carregando={!r}
            aoAbrir={() => { void navegar({ to: '/cadastros/integracoes' }); }}
          />
          <CardIndicador
            rotulo="Em conferência"
            valor={r ? formatarInteiro(r.emConferencia) : ''}
            contexto="lotes de entrada esperando quem promova"
            icone={Hourglass}
            tom="standby"
            carregando={!r}
            aoAbrir={() => { void navegar({ to: '/cadastros/integracoes/lotes' }); }}
          />
          <CardIndicador
            rotulo="Divergências abertas"
            valor={r ? formatarInteiro(r.divergenciasAbertas) : ''}
            contexto="do retorno contra o que saiu"
            icone={Flag}
            tom="standby"
            alerta={(r?.divergenciasAbertas ?? 0) > 0}
            carregando={!r}
            aoAbrir={() => { void navegar({ to: '/cadastros/integracoes/divergencias' }); }}
          />
          <CardIndicador
            rotulo="Trocas atrasadas"
            valor={r ? formatarInteiro(r.trocasAtrasadas) : ''}
            contexto="passaram da frequência da matriz sem lote"
            icone={CalendarClock}
            tom="parada"
            alerta={(r?.trocasAtrasadas ?? 0) > 0}
            carregando={!r}
            aoAbrir={() => { void navegar({ to: '/cadastros/integracoes' }); }}
          />
        </FaixaIndicadores>
        {/* O TRILHO DE ABAS SAIU (pedido do dono, 25/10/2026: *"Eu não quero
            mais abas internas"*). As telas deste módulo são seções da
            navegação lateral, que é a navegação do sistema. A linha que
            explica a tela aberta ficou: ela diz o que esta tela responde, e
            não era parte do trilho. */}
        <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">{EXPLICACAO[aba.para]}</p>
        {children}
      </div>
    </div>
  );
}

/* ============================================================== a matriz */

interface LinhaDaEntidade {
  readonly descricao: DescricaoDaEntidade;
  readonly vigente: LinhaDaMatriz | null;
  readonly propostas: number;
  readonly linhas: readonly LinhaDaMatriz[];
}

const COLUNAS_DA_MATRIZ: readonly Coluna<LinhaDaEntidade>[] = [
  {
    chave: 'entidade',
    rotulo: 'Entidade',
    celula: (e) => (
      <span className="flex flex-wrap items-center gap-2">
        <span className="txt-corpo text-tinta">{ROTULO_DA_ENTIDADE[e.descricao.entidade]}</span>
        {e.descricao.fiscal ? <EtiquetaDaIntegracao>Fiscal</EtiquetaDaIntegracao> : null}
      </span>
    ),
  },
  {
    chave: 'mestre',
    rotulo: 'Mestre e troca',
    quebra: 'duas-linhas',
    celula: (e) => (
      <span className="flex flex-col">
        <TextoDaCelula texto={e.vigente ? fraseDaLinha(e.vigente) : 'Sem linha aprovada'} classe={`txt-corpo ${e.vigente ? 'text-tinta' : 'text-tinta-secundaria'}`} />
        <TextoDaCelula texto={e.vigente ? vigenciaEscrita(e.vigente) : e.descricao.situacao} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'troca',
    rotulo: 'Troca',
    alinhamento: 'status',
    prioridade: 'secundaria',
    celula: (e) => {
      const t = e.vigente ? trocaEscrita(e.vigente) : null;
      return t ? <SeloDaIntegracao rotulo={t.texto.split(' · ')[0] ?? t.texto} tom={t.tom} /> : <span className="txt-apoio text-tinta-secundaria">—</span>;
    },
  },
  {
    chave: 'propostas',
    rotulo: 'Propostas',
    alinhamento: 'numero',
    celula: (e) => <span className="txt-corpo tabular-nums text-tinta">{formatarInteiro(e.propostas)}</span>,
  },
];

export function PaginaMatrizDeIntegracao() {
  const matriz = useMatriz();
  const sistemas = useSistemas();
  const pode = usePermissoesDaIntegracao();
  const [aberta, setAberta] = useState<Entidade | null>(null);
  const [sistemaNovo, setSistemaNovo] = useState(false);
  const [propondo, setPropondo] = useState(false);
  const [decidindo, setDecidindo] = useState<LinhaDaMatriz | null>(null);
  const [encerrando, setEncerrando] = useState<LinhaDaMatriz | null>(null);
  const m = matriz.data;
  const listaDeSistemas = sistemas.data?.sistemas ?? [];
  const linhas: LinhaDaEntidade[] = (m?.entidades ?? []).map((descricao) => {
    const daEntidade = (m?.linhas ?? []).filter((l) => l.entidade === descricao.entidade);
    return {
      descricao,
      vigente: daEntidade.find((l) => l.vigente) ?? null,
      propostas: daEntidade.filter((l) => l.situacao === 'proposta').length,
      linhas: daEntidade,
    };
  });
  const selecionada = linhas.find((l) => l.descricao.entidade === aberta) ?? null;
  return (
    <>
      <HubDasIntegracoes
        acoes={
          pode.configurar ? (
            <>
              <Botao nivel="secundario" onClick={() => { setSistemaNovo(true); }}>Cadastrar sistema</Botao>
              <Botao nivel="primario" disabled={listaDeSistemas.length === 0 && !m} onClick={() => { setPropondo(true); }}>
                <Plus aria-hidden="true" className="size-[var(--icone-controle)]" />
                Propor linha
              </Botao>
            </>
          ) : undefined
        }
      >
        <section className="cartao flex flex-col gap-2 p-6">
          <h2 className="txt-secao text-tinta">Antes de integrar</h2>
          <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">
            {m?.fiscal ?? 'A plataforma não emite nota, não apura imposto e não gera lançamento contábil.'}
            {' A troca segue o contrato v1 (CSV ou JSON, um arquivo por entidade), publicado em /integracoes/contrato/v1: nenhum ERP é nomeado no sistema, e quem integra traduz para ele.'}
          </p>
        </section>
        <Tabela
          rotulo="Matriz de sistema mestre"
          colunas={COLUNAS_DA_MATRIZ}
          linhas={linhas}
          chaveDaLinha={(l) => l.descricao.entidade}
          alturaDupla
          selecionada={aberta}
          aoSelecionar={(l) => { setAberta(l.descricao.entidade); }}
          carregando={matriz.isPending}
          erro={matriz.isError ? <ErroDaIntegracao oQue="a matriz" erro={matriz.error} aoTentar={() => { void matriz.refetch(); }} /> : null}
          vazio={<EstadoVazio icone={Building2} titulo="A matriz ainda não carregou as entidades" descricao="Recarregue a página. Se continuar, o servidor está com a versão anterior e precisa ser atualizado." />}
        />
        <section className="flex flex-col gap-3">
          <h2 className="txt-secao text-tinta">Sistemas externos</h2>
          {sistemas.isPending ? (
            <div className="cartao flex flex-col gap-3 p-6" aria-hidden="true">
              <span className="esqueleto block h-5 w-1/3" />
              <span className="esqueleto block h-4 w-2/3" />
            </div>
          ) : sistemas.isError ? (
            <ErroDaIntegracao oQue="os sistemas" erro={sistemas.error} aoTentar={() => { void sistemas.refetch(); }} />
          ) : listaDeSistemas.length === 0 ? (
            <EstadoVazio
              icone={Building2}
              titulo="Nenhum sistema externo cadastrado"
              descricao={pode.configurar ? 'Cadastre o ERP (ou a contabilidade) para ele poder ser mestre de uma entidade na matriz.' : 'Quem cadastra o sistema externo é o perfil Administrador.'}
              acao={pode.configurar ? <Botao nivel="secundario" onClick={() => { setSistemaNovo(true); }}>Cadastrar sistema</Botao> : undefined}
            />
          ) : (
            <ul className="grid gap-3 min-[1024px]:grid-cols-2" aria-label="Sistemas externos">
              {listaDeSistemas.map((s) => (
                <li key={s.id} className="cartao flex flex-wrap items-center justify-between gap-3 p-4">
                  <span className="flex min-w-0 flex-col">
                    <span className="txt-corpo text-tinta">{s.nome}</span>
                    <span className="txt-apoio text-tinta-secundaria">{`${s.codigo} · ${ROTULO_DO_TIPO_DE_SISTEMA[s.tipo] ?? s.tipo}${s.observacao ? ` · ${s.observacao}` : ''}`}</span>
                  </span>
                  <span className="flex flex-wrap gap-2">
                    {s.demonstracao ? <EtiquetaDaIntegracao>Demonstração</EtiquetaDaIntegracao> : null}
                    <SeloDaIntegracao rotulo={s.ativo ? 'Em uso' : 'Fora de uso'} tom={s.ativo ? 'sucesso' : 'neutro'} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </HubDasIntegracoes>

      {selecionada ? (
        <PainelDetalhe
          titulo={ROTULO_DA_ENTIDADE[selecionada.descricao.entidade]}
          subtitulo={selecionada.vigente ? fraseDaLinha(selecionada.vigente) : 'Sem linha aprovada hoje'}
          marcadores={selecionada.descricao.fiscal ? <EtiquetaDaIntegracao>Fiscal</EtiquetaDaIntegracao> : undefined}
          dadosChave={
            <>
              <DadoChave rotulo="Na versão 1">{selecionada.descricao.situacao}</DadoChave>
              {selecionada.vigente ? <DadoChave rotulo="Contingência">{selecionada.vigente.contingencia}</DadoChave> : null}
            </>
          }
          abas={[
            {
              id: 'linhas',
              rotulo: `Linhas (${formatarInteiro(selecionada.linhas.length)})`,
              conteudo: selecionada.linhas.length === 0 ? (
                <EstadoVazio
                  icone={Building2}
                  titulo="Nenhuma linha para esta entidade"
                  descricao={pode.configurar ? 'Proponha a linha: quem é o mestre, com quem se troca e a contingência. Outra pessoa aprova.' : 'Quem propõe a linha da matriz é o perfil Administrador.'}
                />
              ) : (
                <ul className="flex flex-col" aria-label="Linhas da matriz">
                  {selecionada.linhas.map((l) => (
                    <li key={l.id} className="flex flex-col gap-2 border-t border-borda-sutil py-3 first:border-t-0 first:pt-0">
                      <span className="flex flex-wrap items-center justify-between gap-2">
                        <span className="txt-corpo text-tinta">{fraseDaLinha(l)}</span>
                        <SeloDaIntegracao {...SITUACAO_DA_LINHA[l.situacao]} />
                      </span>
                      <span className="txt-apoio text-pretty text-tinta-secundaria">
                        {`${vigenciaEscrita(l)} · proposta por ${l.propostaPor ?? '—'} em ${formatarDia(l.propostaEm)}`}
                        {l.decididaPor ? ` · decidida por ${l.decididaPor}: ${l.nota ?? ''}` : ''}
                        {l.motivoEncerramento ? ` · encerrada: ${l.motivoEncerramento}` : ''}
                      </span>
                      <span className="txt-apoio text-pretty text-tinta-secundaria">{`Contingência: ${l.contingencia}`}</span>
                      {pode.aprovar && l.situacao === 'proposta' ? (
                        l.propostaPorId === pode.usuarioId ? (
                          <span className="txt-apoio text-atencao-texto">Você propôs esta linha: quem decide é outra pessoa com a licença de aprovar a matriz.</span>
                        ) : (
                          <span><Botao nivel="secundario" tamanho="p" onClick={() => { setDecidindo(l); }}>Decidir proposta</Botao></span>
                        )
                      ) : null}
                      {pode.aprovar && l.situacao === 'aprovada' ? (
                        <span><Botao nivel="fantasma" tamanho="p" onClick={() => { setEncerrando(l); }}>Encerrar vigência</Botao></span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ),
            },
          ]}
          aoFechar={() => { setAberta(null); }}
        />
      ) : null}

      {pode.configurar ? <JanelaDeSistema aberta={sistemaNovo} aoFechar={() => { setSistemaNovo(false); }} /> : null}
      {pode.configurar && m ? (
        <GavetaDaProposta aberta={propondo} aoFechar={() => { setPropondo(false); }} entidades={m.entidades} sistemas={listaDeSistemas} hoje={m.hoje} />
      ) : null}
      {pode.aprovar ? <JanelaDeDecisao alvo={decidindo} aoFechar={() => { setDecidindo(null); }} /> : null}
      {pode.aprovar && m ? <JanelaDeEncerramento alvo={encerrando} aoFechar={() => { setEncerrando(null); }} hoje={m.hoje} /> : null}
    </>
  );
}

/* ================================================================= os lotes */

const rotaDosLotes = getRouteApi('/cadastros/integracoes/lotes');

const COLUNAS_DOS_LOTES: readonly Coluna<Lote>[] = [
  {
    chave: 'arquivo',
    rotulo: 'Arquivo',
    celula: (l) => (
      <span className="flex flex-col">
        <TextoDaCelula texto={l.nomeArquivo} classe="txt-corpo text-tinta" />
        <TextoDaCelula texto={`${formatarDataHora(new Date(l.criadoEm))} · ${l.criadoPor ?? '—'}`} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'entidade',
    rotulo: 'Entidade',
    celula: (l) => (
      <span className="flex flex-col">
        <span className="txt-corpo text-tinta">{ROTULO_DA_ENTIDADE[l.entidade]}</span>
        <TextoDaCelula
          texto={`${l.direcao === 'entrada' ? `entrada de ${l.sistema}` : `saída para ${l.sistema}`}${l.competencia ? ` · ${formatarCompetencia(l.competencia)}, revisão ${String(l.revisao ?? 1)}` : ''}`}
          classe="txt-apoio text-tinta-secundaria"
        />
      </span>
    ),
  },
  {
    chave: 'linhas',
    rotulo: 'Linhas válidas',
    alinhamento: 'numero',
    celula: (l) => <Medida valor={formatarInteiro(l.linhasValidas)} unidade={`de ${formatarInteiro(l.linhas)}`} />,
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    alinhamento: 'status',
    celula: (l) => <SeloDaIntegracao {...SITUACAO_DO_LOTE[l.situacao]} />,
  },
];

export function PaginaLotesDeIntegracao() {
  const busca = rotaDosLotes.useSearch();
  const navegarNaRota = rotaDosLotes.useNavigate();
  const navegar = useNavigate();
  const direcao = busca.direcao;
  const [pagina, setPagina] = useState(1);
  const lotes = useLotes(direcao, pagina);
  const matriz = useMatriz();
  const pode = usePermissoesDaIntegracao();
  const [importando, setImportando] = useState(false);
  const [gerando, setGerando] = useState(false);
  const lista = lotes.data?.lotes ?? [];
  const total = lotes.data?.total ?? 0;
  const porPagina = lotes.data?.porPagina ?? 100;
  const { entrada: mestres, saida: destinos } = mestresDeHoje(matriz.data?.linhas ?? []);
  const abrir = (id: string) => { void navegar({ to: '/cadastros/integracoes/lotes/$loteId', params: { loteId: id } }); };
  return (
    <>
      <HubDasIntegracoes
        acoes={
          <>
            {pode.exportar ? <Botao nivel="secundario" onClick={() => { setGerando(true); }}>Gerar saída</Botao> : null}
            {pode.importar ? (
              <Botao nivel="primario" onClick={() => { setImportando(true); }}>
                <Upload aria-hidden="true" className="size-[var(--icone-controle)]" />
                Importar arquivo
              </Botao>
            ) : null}
          </>
        }
      >
        <div className="flex flex-wrap items-end gap-4">
          <Seletor
            rotulo="Direção"
            valor={direcao ?? 'todos'}
            opcoes={[{ valor: 'todos', rotulo: 'Todos' }, { valor: 'entrada', rotulo: 'Entrada' }, { valor: 'saida', rotulo: 'Saída' }]}
            aoEscolher={(v) => {
              setPagina(1);
              void navegarNaRota({ search: v === 'todos' ? {} : { direcao: v } });
            }}
          />
        </div>
        <Tabela
          rotulo="Lotes de integração"
          colunas={COLUNAS_DOS_LOTES}
          linhas={lista}
          chaveDaLinha={(l) => l.id}
          alturaDupla
          aoSelecionar={(l) => { abrir(l.id); }}
          carregando={lotes.isPending}
          erro={lotes.isError ? <ErroDaIntegracao oQue="os lotes" erro={lotes.error} aoTentar={() => { void lotes.refetch(); }} /> : null}
          vazio={
            direcao ? (
              <EstadoVazio
                icone={SearchX}
                titulo={`Nenhum lote de ${direcao === 'entrada' ? 'entrada' : 'saída'}`}
                descricao="Limpe o filtro para ver todos os lotes."
                acao={<Botao nivel="secundario" onClick={() => { void navegarNaRota({ search: {} }); }}>Limpar filtro</Botao>}
              />
            ) : (
              <EstadoVazio
                icone={Upload}
                titulo="Nenhum lote ainda"
                descricao={pode.importar
                  ? 'Importe o primeiro arquivo do sistema mestre, ou gere a saída de um mês. Antes, a matriz precisa de uma linha aprovada para a entidade.'
                  : 'Quem importa e exporta é o perfil Administrador. Os lotes aparecem aqui depois.'}
              />
            )
          }
          rodape={
            <RodapeTabela
              primeiro={total === 0 ? 0 : (pagina - 1) * porPagina + 1}
              ultimo={Math.min(pagina * porPagina, total)}
              total={total}
              pagina={pagina}
              paginas={Math.max(1, Math.ceil(total / porPagina))}
              porPagina={porPagina}
              opcoesPorPagina={[porPagina]}
              aoMudarPagina={setPagina}
              aoMudarPorPagina={() => undefined}
            />
          }
        />
      </HubDasIntegracoes>
      {pode.importar ? <JanelaDeImportacao aberta={importando} aoFechar={() => { setImportando(false); }} aoImportar={abrir} mestres={mestres} /> : null}
      {pode.exportar && matriz.data ? (
        <JanelaDaSaida aberta={gerando} aoFechar={() => { setGerando(false); }} aoGerar={abrir} hoje={matriz.data.hoje} destinos={destinos} />
      ) : null}
    </>
  );
}

/* ====================================================== as correspondencias */

export function PaginaCorrespondencias() {
  const [busca, setBusca] = useState('');
  const [entidade, setEntidade] = useState<CadastroDeEntrada | ''>('');
  const [pagina, setPagina] = useState(1);
  const lista = useCorrespondencias(busca, entidade || undefined, pagina);
  const sistemas = useSistemas();
  const pode = usePermissoesDaIntegracao();
  const [vinculando, setVinculando] = useState(false);
  const [trocando, setTrocando] = useState<Correspondencia | null>(null);
  const linhas = lista.data?.correspondencias ?? [];
  const total = lista.data?.total ?? 0;
  const porPagina = lista.data?.porPagina ?? 100;
  const filtrado = busca.trim() !== '' || entidade !== '';
  const colunas: readonly Coluna<Correspondencia>[] = [
    {
      chave: 'chave',
      rotulo: 'Chave no sistema de fora',
      celula: (c) => (
        <span className="flex flex-col">
          <span className="codigo text-tinta">{c.chaveExterna}</span>
          <TextoDaCelula texto={c.sistema} classe="txt-apoio text-tinta-secundaria" />
        </span>
      ),
    },
    { chave: 'entidade', rotulo: 'Entidade', celula: (c) => <span className="txt-corpo text-tinta">{ROTULO_DA_ENTIDADE[c.entidade]}</span> },
    {
      chave: 'registro',
      rotulo: 'Cadastro da plataforma',
      celula: (c) => (
        <span className="flex flex-col">
          <span className="codigo text-tinta">{c.registroCodigo ?? '—'}</span>
          <TextoDaCelula texto={c.registroNome ?? 'O cadastro não existe mais: troque a correspondência'} classe="txt-apoio text-tinta-secundaria" />
        </span>
      ),
    },
    {
      chave: 'origem',
      rotulo: 'Origem',
      prioridade: 'secundaria',
      celula: (c) => <TextoDaCelula texto={`${c.origem === 'lote' ? 'Promoção de lote' : 'Vinculada à mão'} · ${formatarDia(c.criadoEm)}`} classe="txt-apoio text-tinta" />,
    },
  ];
  return (
    <>
      <HubDasIntegracoes acoes={pode.configurar ? <Botao nivel="primario" disabled={(sistemas.data?.sistemas ?? []).length === 0} onClick={() => { setVinculando(true); }}>Vincular à mão</Botao> : undefined}>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-72 max-w-full">
            <CampoTexto rotulo="Buscar" valor={busca} aoMudar={(v) => { setBusca(v); setPagina(1); }} placeholder="Chave, código ou nome" aplicado={busca.trim() !== ''} maxLength={120} />
          </div>
          <div className="w-60 max-w-full">
            <CampoSelecao
              rotulo="Entidade"
              valor={entidade}
              aplicado={entidade !== ''}
              rotuloVazio="Todas"
              aoMudar={(v) => { setEntidade(v as CadastroDeEntrada | ''); setPagina(1); }}
              opcoes={CADASTROS_DE_ENTRADA.map((e) => ({ valor: e, rotulo: ROTULO_DA_ENTIDADE[e] }))}
            />
          </div>
        </div>
        <Tabela
          rotulo="Correspondências"
          colunas={colunas}
          linhas={linhas}
          chaveDaLinha={(c) => c.id}
          alturaDupla
          carregando={lista.isPending}
          {...(pode.configurar ? { acoes: (c: Correspondencia) => <Botao nivel="fantasma" tamanho="p" onClick={() => { setTrocando(c); }}>Trocar cadastro</Botao> } : {})}
          erro={lista.isError ? <ErroDaIntegracao oQue="as correspondências" erro={lista.error} aoTentar={() => { void lista.refetch(); }} /> : null}
          vazio={
            filtrado ? (
              <EstadoVazio
                icone={SearchX}
                titulo="Nenhuma correspondência com esses filtros"
                descricao="Limpe a busca ou escolha outra entidade."
                acao={<Botao nivel="secundario" onClick={() => { setBusca(''); setEntidade(''); }}>Limpar filtros</Botao>}
              />
            ) : (
              <EstadoVazio
                icone={Building2}
                titulo="Nenhuma correspondência ainda"
                descricao="Elas nascem quando um lote de entrada é promovido, ou quando você vincula à mão um cadastro que já existia."
              />
            )
          }
          rodape={
            <RodapeTabela
              primeiro={total === 0 ? 0 : (pagina - 1) * porPagina + 1}
              ultimo={Math.min(pagina * porPagina, total)}
              total={total}
              pagina={pagina}
              paginas={Math.max(1, Math.ceil(total / porPagina))}
              porPagina={porPagina}
              opcoesPorPagina={[porPagina]}
              aoMudarPagina={setPagina}
              aoMudarPorPagina={() => undefined}
            />
          }
        />
      </HubDasIntegracoes>
      {pode.configurar ? <JanelaDeCorrespondencia aberta={vinculando} aoFechar={() => { setVinculando(false); }} sistemas={sistemas.data?.sistemas ?? []} /> : null}
      {pode.configurar ? <JanelaDeTroca alvo={trocando} aoFechar={() => { setTrocando(null); }} /> : null}
    </>
  );
}

/* ======================================================== as divergencias */

const rotaDasDivergencias = getRouteApi('/cadastros/integracoes/divergencias');

export function PaginaDivergenciasDaTroca() {
  const busca = rotaDasDivergencias.useSearch();
  const navegarNaRota = rotaDasDivergencias.useNavigate();
  const situacao = busca.situacao ?? 'aberta';
  const [pagina, setPagina] = useState(1);
  const fila = useDivergencias(situacao, pagina);
  const configuracao = useConfiguracaoDaTroca();
  const pode = usePermissoesDaIntegracao();
  const [resolvendo, setResolvendo] = useState<Divergencia | null>(null);
  const [tolerancia, setTolerancia] = useState(false);
  const cliente = useQueryClient();
  const assumir = useMutation({
    mutationFn: (d: Divergencia) => apiDasIntegracoes.assumir(d.id),
    meta: { retorno: retornoDe<{ tipo: Divergencia['tipo'] }>((r) => ({ titulo: 'Divergência assumida', ...retornoDoResponsavel(r, pode.nome) })) },
    onSuccess: () => { void cliente.invalidateQueries({ queryKey: ['integracoes'] }); },
  });
  const linhas = fila.data?.divergencias ?? [];
  const total = fila.data?.total ?? 0;
  const porPagina = fila.data?.porPagina ?? 100;
  const colunas: readonly Coluna<Divergencia>[] = [
    {
      chave: 'tipo',
      rotulo: 'Divergência',
      quebra: 'duas-linhas',
      celula: (d) => (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{ROTULO_DO_TIPO_DE_DIVERGENCIA[d.tipo]}</span>
          <TextoDaCelula texto={`${alvoDaDivergencia(d)} · ${d.sistema}${d.competencia ? `, ${formatarCompetencia(d.competencia)} rev. ${String(d.revisao ?? 1)}` : ''}`} classe="txt-apoio text-tinta-secundaria" linhas={2} />
        </span>
      ),
    },
    { chave: 'plataforma', rotulo: 'Plataforma', alinhamento: 'numero', celula: (d) => <span className="txt-corpo tabular-nums text-tinta">{d.valorPlataforma === null ? '—' : formatarReais(d.valorPlataforma)}</span> },
    { chave: 'externo', rotulo: 'Sistema de fora', alinhamento: 'numero', celula: (d) => <span className="txt-corpo tabular-nums text-tinta">{d.valorExterno === null ? '—' : formatarReais(d.valorExterno)}</span> },
    { chave: 'diferenca', rotulo: 'Diferença', alinhamento: 'numero', celula: (d) => <span className="txt-corpo tabular-nums text-erro-texto">{diferencaEscrita(d.diferenca)}</span> },
    {
      chave: 'quem',
      rotulo: situacao === 'aberta' ? 'Responsável' : 'Resolução',
      prioridade: 'secundaria',
      quebra: 'duas-linhas',
      celula: (d) => (
        <TextoDaCelula
          texto={situacao === 'aberta' ? (d.responsavel ?? 'Ninguém assumiu') : `${ROTULO_DA_RESOLUCAO[d.resolucao ?? ''] ?? ''} · ${d.resolvidaPor ?? 'o sistema'}`}
          classe="txt-apoio text-tinta"
          linhas={2}
        />
      ),
    },
  ];
  return (
    <>
      <HubDasIntegracoes acoes={pode.configurar ? <Botao nivel="secundario" onClick={() => { setTolerancia(true); }}>Mudar tolerância</Botao> : undefined}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <Seletor
            rotulo="Situação"
            valor={situacao}
            opcoes={[{ valor: 'aberta', rotulo: 'Abertas' }, { valor: 'resolvida', rotulo: 'Resolvidas' }]}
            aoEscolher={(v) => {
              setPagina(1);
              void navegarNaRota({ search: v === 'aberta' ? {} : { situacao: v } });
            }}
          />
          <p className="txt-apoio text-tinta-secundaria">
            {configuracao.data ? `Tolerância da empresa: ${formatarReais(configuracao.data.toleranciaReais)}${configuracao.data.toleranciaReais === 0 ? ' (fecha ao centavo)' : ''}` : ''}
          </p>
        </div>
        <Tabela
          rotulo="Divergências da troca"
          colunas={colunas}
          linhas={linhas}
          chaveDaLinha={(d) => d.id}
          alturaDupla
          carregando={fila.isPending}
          {...(pode.conciliar && situacao === 'aberta'
            ? {
              acoes: (d: Divergencia) => (
                <span className="flex flex-wrap gap-2">
                  {d.responsavelId !== pode.usuarioId ? (
                    <Botao nivel="fantasma" tamanho="p" disabled={assumir.isPending} onClick={() => { assumir.mutate(d); }}>Assumir divergência</Botao>
                  ) : null}
                  <Botao nivel="fantasma" tamanho="p" onClick={() => { setResolvendo(d); }}>Resolver divergência</Botao>
                </span>
              ),
            }
            : {})}
          erro={fila.isError ? <ErroDaIntegracao oQue="as divergências" erro={fila.error} aoTentar={() => { void fila.refetch(); }} /> : null}
          vazio={
            situacao === 'aberta' ? (
              <EstadoVazio icone={Flag} titulo="Nenhuma divergência aberta" descricao="O que saiu bate com o que o sistema de fora devolveu, ou nenhum retorno chegou ainda." />
            ) : (
              <EstadoVazio icone={SearchX} titulo="Nenhuma divergência resolvida" descricao="As resolvidas aparecem aqui com a resolução e quem a escreveu." acao={<Botao nivel="secundario" onClick={() => { void navegarNaRota({ search: {} }); }}>Ver as abertas</Botao>} />
            )
          }
          rodape={
            <RodapeTabela
              primeiro={total === 0 ? 0 : (pagina - 1) * porPagina + 1}
              ultimo={Math.min(pagina * porPagina, total)}
              total={total}
              pagina={pagina}
              paginas={Math.max(1, Math.ceil(total / porPagina))}
              porPagina={porPagina}
              opcoesPorPagina={[porPagina]}
              aoMudarPagina={setPagina}
              aoMudarPorPagina={() => undefined}
            />
          }
        />
      </HubDasIntegracoes>
      {pode.conciliar ? <JanelaDeResolucao alvo={resolvendo} aoFechar={() => { setResolvendo(null); }} /> : null}
      {pode.configurar ? <JanelaDaTolerancia aberta={tolerancia} aoFechar={() => { setTolerancia(false); }} atual={configuracao.data?.toleranciaReais ?? 0} /> : null}
    </>
  );
}

